// XRP Ledger — derivation, validation and payment signing.
// Browser-safe: @scure/@noble for keys and hashing, ripple-binary-codec for
// the ledger's own serialization.
//
// WHY THE CODEC IS A DEPENDENCY. Everything else here is a few lines of
// hashing we can check by hand, and the TON adapter shows the house style of
// re-implementing rather than pulling a library in. Serialization is where
// that stops being worth it: XRPL encodes a transaction as typed fields in a
// canonical order, and a field emitted in the wrong place produces a blob that
// still signs, still broadcasts, and pays the wrong thing. The codec IS the
// reference implementation, so it is cheaper to depend on it than to pin a
// copy of it. The equivalence test re-encodes real mainnet transactions and
// checks their published hashes come back, which is the honest way to show
// this path is correct.
//
// WHAT IS NOT LIKE THE OTHER CHAINS: an XRPL account must hold a reserve to
// exist at all. See xrpSpendableDrops and the reserve note there — "balance"
// is not the same question as "how much can I send", and treating them as one
// is how a user ends up unable to move the last XRP they can see.

import { HDKey } from '@scure/bip32';
import { mnemonicToSeedSync } from '@scure/bip39';
import { secp256k1 } from '@noble/curves/secp256k1';
import { sha512 } from '@noble/hashes/sha512';
import { sha256 } from '@noble/hashes/sha256';
import { ripemd160 } from '@noble/hashes/ripemd160';
import { base58xrp, hex } from '@scure/base';
import { encode, encodeForSigning } from 'ripple-binary-codec';
import type { DerivedAddresses } from '../types';

/** BIP-44: m/44'/144'/0'/0/index, secp256k1 — the path every XRPL wallet
 *  that supports mnemonics derives from, so the same phrase shows the same
 *  account elsewhere. */
const PATH = (index: number) => `m/44'/144'/0'/0/${index}`;

/** Classic address type prefix. Not a chain id — XRPL uses it to tell an
 *  account address from the other base58 payloads it encodes. */
const ACCOUNT_PREFIX = 0x00;

/** XRPL hashes are the first half of a SHA-512, never a SHA-256. */
function sha512Half(data: Uint8Array): Uint8Array {
  return sha512(data).slice(0, 32);
}

function prefixed(prefix: number, body: Uint8Array): Uint8Array {
  const out = new Uint8Array(4 + body.length);
  out[0] = (prefix >>> 24) & 0xff;
  out[1] = (prefix >>> 16) & 0xff;
  out[2] = (prefix >>> 8) & 0xff;
  out[3] = prefix & 0xff;
  out.set(body, 4);
  return out;
}

/** Account id: RIPEMD-160 of the SHA-256 of the public key — Bitcoin's hash160,
 *  reached by a different road. */
function accountId(publicKey: Uint8Array): Uint8Array {
  return ripemd160(sha256(publicKey));
}

/** base58check in XRPL's own alphabet, with a double-SHA-256 checksum. */
function encodeClassicAddress(publicKey: Uint8Array): string {
  const payload = new Uint8Array(21);
  payload[0] = ACCOUNT_PREFIX;
  payload.set(accountId(publicKey), 1);
  const checksum = sha256(sha256(payload)).slice(0, 4);
  const full = new Uint8Array(25);
  full.set(payload);
  full.set(checksum, 21);
  return base58xrp.encode(full);
}

/** r… address + compressed pubkey at `index` (mirrors deriveBtcAddresses). */
export function deriveXrpAddresses(seed: Uint8Array, index = 0): DerivedAddresses {
  const child = HDKey.fromMasterSeed(seed).derive(PATH(index));
  if (!child.publicKey) throw new Error('xrp derivation failed: no public key');
  return { eoa: encodeClassicAddress(child.publicKey), publicKey: hex.encode(child.publicKey) };
}

function xrpChild(mnemonic: string, index: number) {
  const child = HDKey.fromMasterSeed(mnemonicToSeedSync(mnemonic)).derive(PATH(index));
  if (!child.privateKey || !child.publicKey) throw new Error('xrp derivation failed');
  return { priv: child.privateKey, pub: child.publicKey };
}

export function xrpAddressFromMnemonic(mnemonic: string, index = 0): string {
  return deriveXrpAddresses(mnemonicToSeedSync(mnemonic), index).eoa;
}

/**
 * Well-formed MAINNET classic address.
 *
 * X-addresses (the 'X…' form, which packs a destination tag into the address)
 * are deliberately NOT accepted. They are valid XRPL addresses and the right
 * long-term answer to tags, but accepting one here while the send path ignores
 * its embedded tag would deliver to the right account with the wrong tag —
 * which at an exchange means the money is not credited. Rejecting is the
 * honest failure; isXrpXAddress below lets the UI say why.
 */
export function isValidXrpAddress(address: string): boolean {
  try {
    const decoded = base58xrp.decode(address);
    if (decoded.length !== 25 || decoded[0] !== ACCOUNT_PREFIX) return false;
    const body = decoded.slice(0, 21);
    const checksum = decoded.slice(21);
    const want = sha256(sha256(body)).slice(0, 4);
    return checksum.every((b, i) => b === want[i]);
  } catch {
    return false;
  }
}

/** An 'X…' address: valid on XRPL, but carrying a tag this send path cannot
 *  honour. Separated from isValidXrpAddress so the UI can explain itself. */
export function isXrpXAddress(address: string): boolean {
  return /^X[1-9A-HJ-NP-Za-km-z]{40,}$/.test(address);
}

/**
 * How much of a balance can actually leave the account.
 *
 * An XRPL account must keep the base reserve to exist, plus an increment for
 * every object it owns, and the network fee comes out on top. The remainder is
 * the only amount a Payment can move — a send of the full balance is rejected
 * by the ledger, so the UI must subtract this before the user tries.
 *
 * The reserves are PARAMETERS, read from the ledger (`server_state` →
 * reserve_base / reserve_inc), not constants: validator fee voting changes
 * them, and it has — the base dropped from 10 XRP to 1 XRP in December 2024.
 * Hard-coding today's value would silently overquote the day it moves again.
 */
export function xrpSpendableDrops(p: {
  balanceDrops: bigint;
  reserveBaseDrops: bigint;
  reserveIncDrops: bigint;
  ownerCount: number;
  feeDrops: bigint;
}): bigint {
  const locked = p.reserveBaseDrops + p.reserveIncDrops * BigInt(Math.max(0, p.ownerCount));
  const free = p.balanceDrops - locked - p.feeDrops;
  return free > 0n ? free : 0n;
}

export interface XrpPaymentParams {
  mnemonic: string;
  index?: number;
  to: string;
  amountDrops: bigint;
  feeDrops: bigint;
  /** Next sequence number for the sending account (account_info). */
  sequence: number;
  /** Ledger after which the payment can no longer be applied. Without it a
   *  signed payment stays replayable forever. */
  lastLedgerSequence: number;
  /** Required by some recipients (exchanges). The ledger rejects a payment
   *  that omits one when the destination sets lsfRequireDestTag. */
  destinationTag?: number;
}

export interface SignedXrpTx {
  /** Signed transaction blob, hex — what `submit` takes. */
  blob: string;
  /** Transaction id, the hash the ledger will know it by. */
  hash: string;
}

/**
 * Build and sign a Payment.
 *
 * Signature is secp256k1 over the SHA-512-half of the signing serialization,
 * DER-encoded. @noble enforces the low-S form by default, which the ledger
 * requires — a high-S signature is rejected as malformed, not as invalid.
 */
export function buildAndSignXrpPayment(p: XrpPaymentParams): SignedXrpTx {
  if (!isValidXrpAddress(p.to)) throw new Error('xrp: invalid recipient address');
  if (p.amountDrops <= 0n) throw new Error('xrp: amount must be positive');
  if (p.feeDrops <= 0n) throw new Error('xrp: fee must be positive');
  if (!Number.isInteger(p.sequence) || p.sequence < 0) throw new Error('xrp: bad sequence');
  if (!Number.isInteger(p.lastLedgerSequence) || p.lastLedgerSequence <= 0) {
    throw new Error('xrp: bad lastLedgerSequence');
  }
  if (p.destinationTag != null) {
    if (!Number.isInteger(p.destinationTag) || p.destinationTag < 0 || p.destinationTag > 0xffffffff) {
      throw new Error('xrp: destination tag must be a uint32');
    }
  }

  const { priv, pub } = xrpChild(p.mnemonic, p.index ?? 0);
  const from = encodeClassicAddress(pub);
  if (from === p.to) throw new Error('xrp: cannot pay yourself');

  const tx: Record<string, unknown> = {
    TransactionType: 'Payment',
    Account: from,
    Destination: p.to,
    // XRP amounts are a decimal string of drops. An object here would mean an
    // issued currency, which is a different asset entirely.
    Amount: p.amountDrops.toString(),
    Fee: p.feeDrops.toString(),
    Sequence: p.sequence,
    LastLedgerSequence: p.lastLedgerSequence,
    SigningPubKey: hex.encode(pub).toUpperCase(),
  };
  if (p.destinationTag != null) tx.DestinationTag = p.destinationTag;

  const signingHash = sha512Half(hex.decode(encodeForSigning(tx).toLowerCase()));
  const signature = secp256k1.sign(signingHash, priv);
  tx.TxnSignature = hex.encode(signature.toDERRawBytes()).toUpperCase();

  const blob = encode(tx);
  const hash = hex
    .encode(sha512Half(prefixed(0x54584e00, hex.decode(blob.toLowerCase()))))
    .toUpperCase();
  return { blob, hash };
}

/** The transaction id of an already-signed blob — same hash the ledger uses. */
export function xrpTxHash(blobHex: string): string {
  return hex
    .encode(sha512Half(prefixed(0x54584e00, hex.decode(blobHex.toLowerCase()))))
    .toUpperCase();
}
